/**
 * Axios 인스턴스 설정
 * - 기본 URL 설정
 * - 인증 토큰 자동 설정
 * - 요청/응답 인터셉터 설정
 */

import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import Config from '../config/constants';
import { handleUnauthorized } from './authInterceptor';

// 사용자 정보 인터페이스
interface User {
  accessToken: string;
  refreshToken?: string;
  user?: {
    id: number;
    email: string;
    name: string;
    role: string;
  };
}

// API 에러 응답 인터페이스
interface ApiErrorResponse {
  error: string;
  message: string;
  statusCode: number;
  timestamp: string;
}

// 사용자 정보를 로컬 스토리지에서 가져오는 함수
const getUserFromStorage = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    
    const user = JSON.parse(userStr);
    return user as User;
  } catch (error) {
    console.error('사용자 정보 파싱 에러:', error);
    return null;
  }
};

// Axios 인스턴스 생성
const axiosInstance: AxiosInstance = axios.create({
  baseURL: Config.API_HOST,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
  timeout: 10000, // 10초 타임아웃
});

// 요청 인터셉터 - 토큰 자동 설정
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const user = getUserFromStorage();
    
    if (user?.accessToken) {
      // 토큰을 헤더에 추가
      config.headers.access_token = user.accessToken;
      
      // Authorization 헤더도 추가 (표준 방식)
      config.headers.Authorization = `Bearer ${user.accessToken}`;
    }
    
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    console.error('요청 인터셉터 에러:', error);
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 에러 처리
axiosInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error: AxiosError<ApiErrorResponse>): Promise<AxiosError> => {
    console.error('API 요청 에러:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
      }
    });
    
    // 401 처리는 `authInterceptor.ts` 한 곳에 있다 — 여기서 같은 로직을 되풀이하면
    // 기본 인스턴스 쪽과 동작이 갈린다(리다이렉트 중복·정리 누락).
    if (error.response?.status === 401) {
      console.warn('인증 토큰이 만료되었습니다. 로그인 페이지로 이동합니다.');
      handleUnauthorized();
    }
    
    // 403 에러 시 권한 부족 처리
    if (error.response?.status === 403) {
      console.warn('접근 권한이 부족합니다.');
      // 필요시 권한 부족 페이지로 리다이렉트
    }
    
    // 500 에러 시 서버 에러 처리
    if (error.response?.status === 500) {
      console.error('서버 내부 에러가 발생했습니다.');
      // 필요시 에러 페이지로 리다이렉트
    }
    
    return Promise.reject(error);
  }
);

export { axiosInstance };
export type { User, ApiErrorResponse }; 